import dayjs from 'dayjs';
import { PropTypes } from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { FormProvider, useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { ConditionalRender } from '@ge/components/conditional-render';
import { DatePicker } from '@ge/components/datepicker';
import { Icon, Icons } from '@ge/components/icon';
import { Input } from '@ge/components/input';
import { Loader } from '@ge/components/loader';
import { Select } from '@ge/components/select';
import { Textarea } from '@ge/components/textarea/textarea';
import { Text } from '@ge/components/typography';
import { DateTimeFormats } from '@ge/models/constants';
import { ErrorNotification } from '@ge/shared/components/tasks/error-notification';
import { getDayEndTime } from '@ge/shared/util';
import { typography } from '@ge/tokens';

import { useManualAdjustmentContext } from '../../context/manual-adjustment-context';
import {
  useContractualReportingMalCategory,
  useAddorEditMalEvent,
} from '../../data-hooks/use-contractual-reporting-events';
import { NotesEnteredRoles } from '../../models';
import { maLegendValues } from '../../models/manual-adjustment-legends';

import AddEventDetailDialog from './add-event-detail-dialog';
import {
  CTAAddButtons,
  CTAContainer,
  DialogLabel,
  DialogValue,
  IconButtonIcon,
  EventCategory,
  EventCategoryName,
  EventCategoryColor,
  EventCategoryStatus,
  StyledButton,
  StyledSelect,
  TimeIcon,
  EventCategoryStatusIcon,
  EventNoteContainerHeading,
  AddNoteFormRow,
  StyledIconButtonIcon,
  DialogDisplay,
  StyledContainer,
  EventNoteRoleIcon,
  EventNoteItemInfo,
  EventNoteItem,
  StyledSpan,
  StyledSpanWithEllipsis,
  EventNoteItemText,
} from './event-dialog-style';

const StyledError = styled(ErrorNotification)`
  margin: 0px 0 14px 0px;
`;

const ErrorText = styled(Text).attrs(() => ({
  type: typography.textTypes.body,
  level: 2,
}))`
  margin: 2px -3px -10px -2px;
  color: ${(props) => props.theme.dangerColor};
  display: block;
  .custom-body-2 {
    font-size: 11px;
    line-height: 10px;
  }
`;

const IconButton = styled.button`
  color: ${(props) => props.theme.analyze.manualAdjustment.eventDialog.editButtonColor};
  font-size: 11px;
  font-weight: ${typography.weight.medium};
  line-height: 13px;
  text-transform: uppercase;
  padding: 0px;
`;

const CalendarIcon = styled(Icon).attrs(({ theme }) => ({
  size: 12,
  color: theme.analyze.header.calendarIconColor,
  icon: Icons.CALENDAR,
}))`
  margin-left: 6px;
  vertical-align: initial;
`;
const DateValue = styled.div`
  font-size: 11px;
  line-height: 20px;
  text-align: center;
  padding: 2px 14px;
  border-radius: 2px;
  max-width: 100%;
  display: flex;
  align-items: center;
  cursor: pointer;
  background: ${(props) => props.theme.button.primary.backgroundColor};
  border: ${(props) => props.theme.button.primary.border};
  color: ${(props) => props.theme.button.primary.textColor};
  text-shadow: ${(props) => props.theme.button.primary.textShadow};

  &.react-datepicker__tab-loop
    .react-datepicker-popper
    .react-datepicker
    .react-datepicker__children-container {
    width: 10.8rem !important;
  }
`;

const getTimezoneOffsetUsingDate = (timezone, selectedDate) => {
  if (!timezone) return 0;
  return dayjs(selectedDate).tz(timezone).utcOffset();
};

const getConvertedTime = (time, calendarDate, timezone) => {
  let selectedTime = time;

  if (!dayjs(time).isSame(dayjs(calendarDate))) {
    selectedTime = dayjs(calendarDate)
      .set('hour', dayjs(time).get('hour'))
      .set('minute', dayjs(time).get('minute'))
      .set('second', dayjs(time).get('second')).$d;
  }
  const selectedDate = `${selectedTime.toString().split('GMT')[0]} GMT`;
  let timeZoneOffset = getTimezoneOffsetUsingDate(timezone, selectedDate) / 60;
  timeZoneOffset = timeZoneOffset > 0 ? `+${timeZoneOffset}` : timeZoneOffset;
  const timeString = `${selectedDate}${timeZoneOffset}`;
  return Date.parse(timeString) / 1000;
};

const formatTime = (e) => {
  const input = e.target;
  const tempInputValue = input.value;
  const selectionStart = input.selectionStart;

  // Remove Alphabetic and special characters
  let cleanValue = tempInputValue.replace(/[^0-9]/g, '');

  // Add colons after every two digits to match hh:mm:ss format
  if (cleanValue.length > 2) {
    cleanValue = cleanValue.slice(0, 2) + ':' + cleanValue.slice(2);
  }
  if (cleanValue.length > 5) {
    cleanValue = cleanValue.slice(0, 5) + ':' + cleanValue.slice(5);
  }

  // Limit to hh:mm:ss format (max length 8 characters)
  cleanValue = cleanValue.slice(0, 8);

  // Split into hours, minutes, and seconds
  const parts = cleanValue.split(':');
  let hours = parts[0] || '';
  let minutes = parts[1] || '';
  let seconds = parts[2] || '';

  // Validate each part
  if (hours.length === 2) {
    hours = Math.min(parseInt(hours, 10), 23).toString().padStart(2, '0');
  }
  if (minutes.length === 2) {
    minutes = Math.min(parseInt(minutes, 10), 59).toString().padStart(2, '0');
  }
  if (seconds.length === 2) {
    seconds = Math.min(parseInt(seconds, 10), 59).toString().padStart(2, '0');
  }

  // Join the split parts
  const formattedValue = [hours, minutes, seconds].filter(Boolean).join(':');

  // Calculate new cursor position
  let cursorPosition = selectionStart;
  if (formattedValue.length > tempInputValue.length && selectionStart > 2 && selectionStart <= 5) {
    cursorPosition += 1;
  } else if (formattedValue.length > tempInputValue.length && selectionStart > 5) {
    cursorPosition += 1;
  }

  input.value = formattedValue;
  input.setSelectionRange(cursorPosition, cursorPosition);

  return formattedValue;
};

export const EditEventDetailDialog = ({
  calculateDuration,
  setEditMode,
  timezone,
  showMalEvents,
  assetEventsKind,
  editEventUpdate,
}) => {
  const { t, ready } = useTranslation(['analyze.manual-adjustment'], {
    useSuspense: false,
  });

  const { calendarDate, selectedEvent, selectedSite, selectedAssets } =
    useManualAdjustmentContext();
  const { event } = selectedEvent;
  const [malEventsData, setMalEventsData] = useState([]);
  const { data: malCategoryData, isLoading: malCategoryLoading } =
    useContractualReportingMalCategory({
      siteId: selectedSite.id,
    });

  const onSuccess = useCallback(
    (response) => {
      setEditMode(false);
      editEventUpdate(response[0]?.value?.malId || event?.malId);
      showMalEvents(malEventsData, malCategoryData);
    },
    [event?.malId, malCategoryData, malEventsData, setEditMode, editEventUpdate, showMalEvents],
  );

  const { createAndUpdate, isError, isLoading } = useAddorEditMalEvent({
    siteId: selectedSite.id,
    onSuccess,
  });

  const methods = useForm({ mode: 'onChange' });

  const { control, handleSubmit } = methods;
  const [selectEventCategory, setSelectEventCategory] = useState(
    event.type === 'MAL' ? event.eventCategory : '',
  );
  const [selectEventName, setSelectEventName] = useState(
    event.type === 'MAL' ? event.malCategory : '',
  );
  const childRef = useRef();
  const [addCounter, setAddCounter] = useState(0);
  const startTime = dayjs(event.startTime).tz(timezone); // dayjs(event.startTime).utc(true).tz(timezone);
  const endTime = dayjs(
    getDayEndTime(event.endTime, timezone, calendarDate, event?.type === 'IEC'),
  ).tz(timezone); // dayjs(event.endTime).utc(true).tz(timezone);
  const [editStartTime, setEditStartTime] = useState(dayjs(startTime).$d);
  const [editEndTime, setEditEndTime] = useState(dayjs(endTime).$d);
  const [noteEntered, setNoteEntered] = useState(
    event.notes?.find((note) => note.notesEnteredByRole === NotesEnteredRoles.VERNOVA)
      ?.notesEntered || '',
  );
  const [showStartTime, setShowStartTime] = useState(dayjs(startTime).format('HH:mm:ss'));
  const [showEndTime, setShowEndTime] = useState(dayjs(endTime).format('HH:mm:ss'));
  const [eventDate, setEventDate] = useState({
    startDate: dayjs(event.startTime).tz(timezone).startOf('day').valueOf(),
    endDate: dayjs(endTime).tz(timezone).startOf('day').valueOf(),
  });
  const [timeDuration, setTimeDuration] = useState();
  const [dateError, setDateError] = useState(false);
  const datepickerRef = useRef();

  useEffect(() => {
    setTimeDuration(
      calculateDuration(eventDate.startDate, editStartTime, eventDate.endDate, editEndTime),
    );
  }, [editStartTime, editEndTime, calculateDuration, eventDate.startDate, eventDate.endDate]);

  const handleEventNameChange = useCallback(
    ({ value }) => {
      // eslint-disable-next-line no-unsafe-optional-chaining
      const { eventCategory } = malCategoryData?.find((data) => data.id === value);
      setSelectEventCategory(eventCategory);
      setSelectEventName(value);
    },
    [malCategoryData],
  );
  const user_details = JSON.parse(
    Object.values(sessionStorage).filter((key) => key.includes('profile'))[0],
  );

  const categoryOptions = useMemo(
    () =>
      maLegendValues[assetEventsKind]?.map((e) => ({
        label: t(`legend.${e?.a11yKey}.dialog_title`),
        value: e.category,
      })) || [],
    [assetEventsKind, t],
  );

  const eventOptions = useMemo(
    () =>
      typeof malCategoryData === 'object'
        ? malCategoryData?.map((e) => ({
            label: e.description,
            value: e.id,
          }))
        : [],
    [malCategoryData],
  );

  const onEditStartTimeChange = (value) => {
    setEditStartTime(value);
    setTimeDuration(calculateDuration(eventDate.startDate, value, eventDate.endDate, editEndTime));
  };
  const onEditEndTimeChange = (value) => {
    setEditEndTime(value);
    setTimeDuration(
      calculateDuration(eventDate.startDate, editStartTime, eventDate.endDate, value),
    );
  };
  const onEditNoteChange = (value) => {
    setNoteEntered(value);
  };

  const timeDurationError = useCallback(() => {
    return (
      <ErrorText>
        <span className="custom-body-2">
          {t(
            'event_details_dialog.invalid_starttime_error',
            ' Start time should be less than end time.',
          )}
        </span>
      </ErrorText>
    );
  }, [t]);

  const eventDateError = useMemo(() => {
    return (
      <ErrorText>
        <span className="custom-body-2">
          {t(
            'event_details_dialog.invalid_startDate_error',
            'Start Date should be less than today.',
          )}
        </span>
      </ErrorText>
    );
  }, [t]);

  const onSubmit = useCallback(
    (values) => {
      if (
        dayjs(
          `${dayjs(eventDate.startDate).format(DateTimeFormats.ENDPOINT_PARAM)} ${dayjs(
            values.editStartTime,
          ).format('HH:mm:ss')}`,
        ) >
        dayjs(
          `${dayjs(eventDate.endDate).format(DateTimeFormats.ENDPOINT_PARAM)} ${dayjs(
            values.editEndTime,
          ).format('HH:mm:ss')}`,
        )
      ) {
        setTimeDuration(timeDurationError);
        return false;
      }
      if (
        dayjs(eventDate.startDate).isAfter(dayjs(dayjs().startOf('date').subtract(1, 'day')), 'day')
      ) {
        return false;
      }
      const editMALCategory =
        typeof values.editMALCategory === 'object'
          ? values.editMALCategory.value
          : values.editMALCategory;
      const eventCategory = malCategoryData?.filter((data) => data.id === editMALCategory);
      const assetIds = selectedAssets.length > 0 ? selectedAssets : [selectedEvent.id];
      let malEvents = [
        {
          startTime: getConvertedTime(values.editStartTime, eventDate.startDate, timezone),
          endTime: getConvertedTime(values.editEndTime, eventDate.endDate, timezone),
          malId: event.malId,
          assetId: assetIds,
          adjustedBy: user_details ? user_details.profile.email : '',
          adjustedTime: getConvertedTime(dayjs().$d, calendarDate, timezone),
          day: dayjs(eventDate.startDate).format(DateTimeFormats.ENDPOINT_PARAM),
          eventCategory: eventCategory[0]?.eventCategory || event.eventCategory,
          malCategory: editMALCategory || event.malCategory,
          noteEntered: values.editMALNote,
        },
      ];
      for (let index = 0; index < addCounter; index++) {
        let tempStartDate = values[`addEventDate${index}`]?.startDate;
        let tempEndDate = values[`addEventDate${index}`]?.endDate;
        let tempStartTime = values[`addStartTime${index}`];
        let tempEndTime = values[`addEndTime${index}`];
        if (
          dayjs(tempStartDate).isAfter(dayjs(dayjs().startOf('date').subtract(1, 'day')), 'day')
        ) {
          return false;
        }
        if (
          dayjs(
            `${dayjs(tempStartDate).format(DateTimeFormats.ENDPOINT_PARAM)} ${dayjs(
              tempStartTime,
            ).format('HH:mm:ss')}`,
          ) >
          dayjs(
            `${dayjs(tempEndDate).format(DateTimeFormats.ENDPOINT_PARAM)} ${dayjs(
              tempEndTime,
            ).format('HH:mm:ss')}`,
          )
        ) {
          childRef.current.setTimeDuration(timeDurationError);
          return false;
        }
        const eventCategory = malCategoryData?.filter(
          (data) => data.id === values[`addMALCategory${index}`].value,
        );
        malEvents.push({
          startTime: getConvertedTime(tempStartTime, tempStartDate, timezone),
          endTime: getConvertedTime(tempEndTime, tempEndDate, timezone),
          assetId: assetIds,
          adjustedBy: user_details ? user_details.profile.email : '',
          adjustedTime: getConvertedTime(dayjs().$d, calendarDate, timezone),
          day: dayjs(tempStartDate).format(DateTimeFormats.ENDPOINT_PARAM),
          eventCategory: eventCategory[0]?.eventCategory,
          malCategory: values[`addMALCategory${index}`].value,
          noteEntered: values[`addMALNote${index}`].value || '',
        });
      }
      setMalEventsData(malEvents);
      const isEditMalEvent = Boolean(malEvents[0]?.malId);
      createAndUpdate({
        editMalEvents: isEditMalEvent ? [malEvents[0]] : [],
        addMalEvents: [...(isEditMalEvent ? [] : [malEvents[0]]), ...malEvents.slice(1)],
      });
    },
    [
      eventDate,
      malCategoryData,
      selectedAssets,
      selectedEvent.id,
      timezone,
      event.malId,
      event.eventCategory,
      event.malCategory,
      user_details,
      calendarDate,
      createAndUpdate,
      timeDurationError,
      addCounter,
    ],
  );

  const getCustomDateString = () => {
    const start = dayjs(eventDate.startDate).format(DateTimeFormats.DEFAULT_DATE);
    const end = eventDate.endDate && dayjs(eventDate.endDate).format(DateTimeFormats.DEFAULT_DATE);

    return `${start} ${end && start !== end ? `- ${end}` : ''}`;
  };

  if (!ready || malCategoryLoading) {
    return <Loader />;
  }

  return (
    <>
      <FormProvider {...methods}>
        <ConditionalRender shouldRender={isError}>
          <StyledError />
        </ConditionalRender>
        <EventCategory>
          <EventCategoryColor
            backgroundColor={
              maLegendValues[assetEventsKind]?.find(
                ({ category }) => category === selectEventCategory,
              )?.color
            }
            size={'10px'}
          />
          <EventCategoryName>
            {categoryOptions?.find(({ value }) => value == selectEventCategory)?.label ??
              t('event_details_dialog.eventcategory_placeholder', 'Event Category')}
          </EventCategoryName>
          <EventCategoryStatus>
            <EventCategoryStatusIcon status={event.status?.statusState} />
            {event.status?.statusState || '-'}
          </EventCategoryStatus>
        </EventCategory>
        <StyledSelect>
          <Controller
            control={control}
            name="editMALCategory"
            id="editMALCategory"
            rules={{
              required: true,
            }}
            defaultValue={selectEventName ?? null}
            render={({ onChange }) => (
              <Select
                onChange={(val) => {
                  onChange(val);
                  handleEventNameChange(val);
                }}
                name="editMALCategory"
                placeholder={t(
                  'event_details_dialog.dropdown_placeholder',
                  'Please select an option',
                )}
                options={eventOptions}
                value={eventOptions && eventOptions?.find(({ value }) => value == selectEventName)}
                error={methods.errors?.editMALCategory?.type}
              />
            )}
          />
        </StyledSelect>
        <DialogDisplay>
          <div>
            <DialogLabel>
              {t('event_details_dialog.event_duration_date', 'Event Duration Date')}
            </DialogLabel>
            <DialogValue>
              <Controller
                control={control}
                name={`addEventDate`}
                defaultValue={eventDate}
                rules={{
                  required: true,
                }}
                render={({ onChange }) => (
                  <DatePicker
                    selectsRange
                    className={'custom_datepicketstyle'}
                    startDate={eventDate.startDate}
                    endDate={eventDate.endDate}
                    onChange={(date) => {
                      onChange({
                        startDate: date[0],
                        endDate: date[1],
                      });
                      setEventDate({
                        startDate: date[0],
                        endDate: date[1],
                      });
                      if (
                        dayjs(date[0]).isAfter(
                          dayjs(dayjs().startOf('date').subtract(1, 'day')),
                          'day',
                        )
                      ) {
                        setDateError(true);
                      } else setDateError(false);
                    }}
                    ref={datepickerRef}
                    popperPlacement="bottom-start"
                    customInput={
                      <DateValue
                        onClick={() => {
                          datepickerRef.current.setOpen(true);
                        }}
                      >
                        {getCustomDateString()} <CalendarIcon />
                      </DateValue>
                    }
                    onCalendarClose={() => {
                      if (!dayjs(eventDate.endDate).isValid()) {
                        setEventDate((prev) => ({
                          ...prev,
                          endDate: prev.startDate,
                        }));
                        onChange({
                          startDate: eventDate.startDate,
                          endDate: eventDate.startDate,
                        });
                      }
                    }}
                    onApplyClick={() => {
                      datepickerRef.current.setOpen(false);
                    }}
                  />
                )}
              />
            </DialogValue>
            {dateError && <DialogValue>{eventDateError}</DialogValue>}
          </div>
          <StyledContainer>
            <div>
              <DialogLabel>{t('event_details_dialog.event_start_time', 'Start Time')}</DialogLabel>
              <StyledContainer>
                <Controller
                  control={control}
                  name="editStartTime"
                  defaultValue={editStartTime}
                  render={({ onChange }) => (
                    <DatePicker
                      showTimeSelect
                      dateFormat={DateTimeFormats.DEFAULT_TIME_W_SECS}
                      timeFormat="HH:mm:ss"
                      onChange={(val) => {
                        onChange(val);
                        onEditStartTimeChange(val);
                      }}
                      customInput={
                        <div className="date-picker-member">
                          <Input
                            type="text"
                            placeholder="hh:mm:ss"
                            maxLength={8}
                            steps={2}
                            value={showStartTime}
                            onChange={(e) => setShowStartTime(formatTime(e))}
                          />
                        </div>
                      }
                      selected={editStartTime}
                      timeIntervals={1}
                      open={false}
                    />
                  )}
                />
                <IconButton>
                  <TimeIcon />
                </IconButton>
              </StyledContainer>
            </div>
            <div>
              <DialogLabel>{t('event_details_dialog.event_end_time', 'End Time')}</DialogLabel>
              <StyledContainer>
                <Controller
                  control={control}
                  name="editEndTime"
                  defaultValue={editEndTime}
                  rules={{
                    required: true,
                    validate:
                      showEndTime.toString().replace(/:/g, '').length > 1
                        ? true
                        : 'Time must be in HH:MM:SS format',
                  }}
                  render={({ onChange }) => (
                    <DatePicker
                      showTimeSelect
                      dateFormat={DateTimeFormats.DEFAULT_TIME_W_SECS}
                      timeFormat="HH:mm:ss"
                      onChange={(val) => {
                        onChange(val);
                        onEditEndTimeChange(val);
                      }}
                      customInput={
                        <div className="date-picker-member">
                          <Input
                            type="text"
                            placeholder="hh:mm:ss"
                            maxLength={8}
                            steps={2}
                            value={showEndTime}
                            onChange={(e) => {
                              setShowEndTime(formatTime(e));
                            }}
                            error={showEndTime.toString().replaceAll(':', '').length < 1}
                          />
                        </div>
                      }
                      selected={editEndTime}
                      timeIntervals={1}
                      open={false}
                    />
                  )}
                />
                <IconButton>
                  <TimeIcon />
                </IconButton>
              </StyledContainer>
            </div>
          </StyledContainer>
          <div>
            <DialogLabel>{t('event_details_dialog.event_duration', 'Duration')}</DialogLabel>
            <DialogValue>{timeDuration}</DialogValue>
          </div>
        </DialogDisplay>
        <AddNoteFormRow>
          <EventNoteContainerHeading>
            <StyledIconButtonIcon className="notesIcon" icon={Icons.NOTE} size={12} />
            {t(`legend.${event?.a11yKey}.notes`, 'NOTES')}
          </EventNoteContainerHeading>
          {event.notes
            ?.filter((note) => note.notesEnteredByRole === NotesEnteredRoles.CUSTOMER)
            ?.map((note, index) => {
              return (
                <EventNoteItem key={index}>
                  <EventNoteItemInfo>
                    <StyledSpan>
                      <EventNoteRoleIcon role={note.notesEnteredByRole} />
                      {dayjs(note.notesEnteredTime).format(DateTimeFormats.NOTIFICATION_TIME)}
                    </StyledSpan>
                    <StyledSpanWithEllipsis style={{ maxWidth: '50%' }}>
                      {note.notesEnteredBy}
                    </StyledSpanWithEllipsis>
                  </EventNoteItemInfo>
                  <EventNoteItemText>{note.notesEntered}</EventNoteItemText>
                </EventNoteItem>
              );
            })}
          <Controller
            control={control}
            name="editMALNote"
            id="editMALNote"
            defaultValue={noteEntered}
            render={({ onChange }) => (
              <Textarea
                placeholder={t('notes.description_placeholder', 'Add a note')}
                name="editMALNote"
                value={noteEntered}
                onChange={(event) => {
                  const val = event.target.value;
                  onChange(val);
                  onEditNoteChange(val);
                }}
              />
            )}
          />
        </AddNoteFormRow>
        {[...Array(addCounter)].map((e, i) => (
          <AddEventDetailDialog
            malCategoryOption={malCategoryData}
            index={i}
            key={i}
            event={{ ...event }}
            eventDateDuration={eventDate}
            setEditMode={setEditMode}
            categoryOptions={categoryOptions}
            eventOptions={eventOptions}
            calculateDuration={calculateDuration}
            timezone={timezone}
            formatTime={formatTime}
            ref={childRef}
            assetEventsKind={assetEventsKind}
            noteEntered={noteEntered}
          />
        ))}
        <CTAContainer>
          <CTAAddButtons>
            <div>
              <IconButton type="button" onClick={() => setAddCounter(addCounter + 1)}>
                <IconButtonIcon icon={Icons.ADD} />
                {t('event_details_dialog.add_event_button', 'Add Event')}
              </IconButton>
            </div>
            <div>
              <StyledButton onClick={() => setEditMode(false)}>
                {t('event_details_dialog.cancel_button', 'Cancel')}
              </StyledButton>
            </div>
            <div>
              <StyledButton disabled={isLoading} primary onClick={handleSubmit(onSubmit)}>
                {t('event_details_dialog.save_button', 'Save')}
              </StyledButton>
            </div>
          </CTAAddButtons>
        </CTAContainer>
      </FormProvider>
    </>
  );
};

EditEventDetailDialog.propTypes = {
  calculateDuration: PropTypes.func.isRequired,
  setEditMode: PropTypes.func.isRequired,
  timezone: PropTypes.string,
  showMalEvents: PropTypes.func.isRequired,
  editEventUpdate: PropTypes.func.isRequired,
  assetEventsKind: PropTypes.string,
};
